import { useCallback, useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiJson } from "@/lib/api";

interface Lien { code: string; url: string; canal: string; campagne: string; label: string; court: string; clics: number; visiteurs_uniques: number; clics_7j: number; apercus: number; dernier_clic: string | null; created_at: string }
const CANAUX = ["linkedin", "facebook", "instagram", "youtube", "tiktok", "email", "autre"];

export default function LiensSuivis() {
  const [liens, setLiens] = useState<Lien[]>([]);
  const [f, setF] = useState({ url: "https://grafton-ia.com/", canal: "linkedin", campagne: "", label: "" });
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    try { setLiens((await apiJson<{ liens: Lien[] }>("/api/liens")).liens); } catch { toast.error("Liste indisponible, réessayez."); }
    setChargement(false);
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const creer = async () => {
    try {
      const l = await apiJson<Lien & { court: string }>("/api/liens", { method: "POST", body: JSON.stringify(f) });
      try { await navigator.clipboard.writeText(l.court); toast.success("Lien créé et copié : " + l.court); } catch { toast.success("Lien créé : " + l.court); }
      setF({ ...f, campagne: "", label: "" });
      await charger();
    } catch { toast.error("Adresse refusée : seules les pages de nos propres sites (https) peuvent être suivies."); }
  };
  const copier = async (t: string) => { try { await navigator.clipboard.writeText(t); toast.success("Copié"); } catch { toast.error("Copie impossible"); } };
  const supprimer = async (code: string) => { try { await apiJson(`/api/liens/${code}`, { method: "DELETE" }); await charger(); } catch { toast.error("Suppression impossible"); } };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar />
        <main className="flex-1 min-w-0 overflow-y-auto">
          <header className="h-14 flex items-center gap-3 border-b border-border/50 px-4"><SidebarTrigger /><Link2 className="h-5 w-5 text-primary" /><h1 className="text-lg font-semibold">Liens suivis</h1></header>
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="liens-suivis">
            <Card>
              <CardHeader><CardTitle className="text-base">Créer un lien suivi</CardTitle>
                <CardDescription>Collez ce lien court dans votre publication au lieu de l'adresse directe : Grafton compte les clics lui-même, sans dépendre de LinkedIn ou de Facebook. Les aperçus générés par les réseaux sont comptés à part, et nous ne conservons pas l'adresse IP des visiteurs.</CardDescription></CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label className="text-xs">Page de destination (nos propres sites)</Label><Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} /></div>
                <div><Label className="text-xs">Canal</Label>
                  <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.canal} onChange={(e) => setF({ ...f, canal: e.target.value })}>{CANAUX.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><Label className="text-xs">Campagne (facultatif)</Label><Input value={f.campagne} onChange={(e) => setF({ ...f, campagne: e.target.value })} placeholder="ex. ateliers-ia-novembre" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Nom du lien (facultatif)</Label><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="ex. Post du 20 octobre" /></div>
                <div className="sm:col-span-2"><Button onClick={creer} data-testid="lien-creer">Créer et copier le lien</Button></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Vos liens</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                {chargement ? <p className="text-sm text-muted-foreground">Chargement…</p> : liens.length === 0 ? <p className="text-sm text-muted-foreground">Aucun lien pour l'instant.</p> : (
                  <table className="w-full text-sm"><thead><tr className="text-left text-[11px] text-muted-foreground"><th className="py-1 pr-3">Lien</th><th>Canal</th><th>Clics</th><th>Visiteurs</th><th>7 jours</th><th>Aperçus</th><th /></tr></thead>
                    <tbody>{liens.map((l) => (
                      <tr key={l.code} className="border-t border-border/40 align-top">
                        <td className="py-2 pr-3"><div className="font-medium">{l.label || l.campagne || l.code}</div><button onClick={() => copier(l.court)} className="text-xs text-primary inline-flex items-center gap-1 hover:underline">{l.court}<Copy className="h-3 w-3" /></button></td>
                        <td>{l.canal}</td><td className="font-semibold">{l.clics}</td><td>{l.visiteurs_uniques}</td><td>{l.clics_7j}</td><td className="text-muted-foreground">{l.apercus}</td>
                        <td><button aria-label="Supprimer" onClick={() => supprimer(l.code)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>))}</tbody></table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
